/**
 * Moving a cursor between things laid out on a plane with a stick or a D-pad: board cells drawn
 * on the screen, gems on a tablet, buttons in a dialog. Shared so a hexagon, a square and a button
 * row all answer a push the same way, and kept free of the DOM and the engine so it can be tested.
 */

export interface Point {
  x: number;
  y: number;
}

/** Pushes more than this far off centre count as a direction; below it the stick is at rest. */
export const STEP_THRESHOLD = 0.5;
/** Half-angle of the cone a candidate must lie in to be "in that direction". */
const CONE_COS = Math.cos((70 * Math.PI) / 180);

/**
 * The point that best answers a push in `direction` from `from`: the nearest one inside a cone
 * around the direction, where straying from the line costs distance. On a grid this is the
 * neighbour in that direction (a diagonal push on squares reaches the diagonal, six directions
 * work on hexes), and among scattered buttons it is what a reader would call "the one to the
 * right". `null` when nothing lies that way.
 */
export function nextInDirection(
  points: readonly Point[],
  from: Point,
  direction: Point,
  skip?: (index: number) => boolean,
): number | null {
  const length = Math.hypot(direction.x, direction.y);
  if (length < 1e-6) return null;
  const dx = direction.x / length;
  const dy = direction.y / length;
  let best: number | null = null;
  let bestScore = Infinity;
  points.forEach((p, i) => {
    if (skip?.(i)) return;
    const vx = p.x - from.x;
    const vy = p.y - from.y;
    const distance = Math.hypot(vx, vy);
    if (distance < 1e-6) return;
    const cos = (vx * dx + vy * dy) / distance;
    if (cos < CONE_COS) return;
    // Distance along the push, stretched by how far off the line the candidate sits.
    const score = distance / (cos * cos);
    if (score < bestScore) {
      bestScore = score;
      best = i;
    }
  });
  return best;
}

/** The point nearest to `at`, or `null` for no points. */
export function nearestPoint(
  points: readonly Point[],
  at: Point,
  skip?: (index: number) => boolean,
): number | null {
  let best: number | null = null;
  let bestDistance = Infinity;
  points.forEach((p, i) => {
    if (skip?.(i)) return;
    const distance = Math.hypot(p.x - at.x, p.y - at.y);
    if (distance < bestDistance) {
      bestDistance = distance;
      best = i;
    }
  });
  return best;
}

const D = Math.SQRT1_2;
/**
 * The eight directions from +x round towards +y, exact. Worked out from the angle, a stick held
 * left gave a different point each time its idle axis jittered across zero (+π against −π), and
 * each counted as a new push (client-input:bugs#2).
 */
const OCTANTS: readonly Point[] = [
  { x: 1, y: 0 },
  { x: D, y: D },
  { x: 0, y: 1 },
  { x: -D, y: D },
  { x: -1, y: 0 },
  { x: -D, y: -D },
  { x: 0, y: -1 },
  { x: D, y: -D },
];

/** A stick reading turned into one of eight directions, or `null` while it rests. */
export function stepDirection(x: number, y: number, threshold = STEP_THRESHOLD): Point | null {
  if (Math.hypot(x, y) < threshold) return null;
  const octant = ((Math.round(Math.atan2(y, x) / (Math.PI / 4)) % 8) + 8) % 8;
  const direction = OCTANTS[octant]!;
  return { x: direction.x, y: direction.y };
}
